import React, { useState, useEffect, useRef } from 'react';
import {
  X,
  Download,
  CheckCircle,
  AlertCircle,
  Mail,
  Paperclip,
  Database,
} from 'lucide-react';
import apiService from '../services/api';
import logger from '../utils/logger';

interface PullCasesModalProps {
  isOpen: boolean;
  onClose: (wasDownloadCompleted?: boolean) => void;
  onSuccess: () => void;
}

type DownloadStage =
  | 'preparing'
  | 'connecting'
  | 'downloading'
  | 'processing'
  | 'animating'
  | 'complete'
  | 'error';

const PullCasesModal: React.FC<PullCasesModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [stage, setStage] = useState<DownloadStage>('preparing');
  const [progress, setProgress] = useState(0);
  const [currentMessage, setCurrentMessage] = useState(
    'Preparing to download cases...'
  );
  const [caseResults, setCaseResults] = useState<any[]>([]);
  const [animatedIndex, setAnimatedIndex] = useState(0);
  const [summary, setSummary] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  // Input for specific case numbers (comma-separated)
  const [specificCases, setSpecificCases] = useState<string>('');

  // CRITICAL: Prevent infinite loop - track if download is already in progress
  const isDownloadingRef = useRef(false);
  const hasDownloadedRef = useRef(false);

  useEffect(() => {
    if (isOpen) {
      logger.debug('Modal opened, awaiting user to start download');
    } else {
      logger.debug('Modal closing, resetting state');
      setStage('preparing');
      setProgress(0);
      setCurrentMessage('Preparing to download cases...');
      setCaseResults([]);
      setAnimatedIndex(0);
      setSummary(null);
      setError(null);
      setSpecificCases(''); // Clear input field
      isDownloadingRef.current = false;
      hasDownloadedRef.current = false;
    }
  }, [isOpen]); // ONLY depends on isOpen - nothing else!

  const startDownload = async () => {
    if (isDownloadingRef.current) {
      logger.error('Download already in progress, aborting');
      return;
    }

    logger.debug('Start download requested');
    isDownloadingRef.current = true; // Mark as downloading
    hasDownloadedRef.current = true; // Mark that we've downloaded (prevents re-download)

    try {
      // Stage 1: Preparing (10%)
      setStage('preparing');
      setProgress(10);
      setCurrentMessage('Preparing to download cases from Dynamics...');
      await sleep(500);

      // Stage 2: Connecting (20%)
      setStage('connecting');
      setProgress(20);
      setCurrentMessage('Connecting to Dynamics 365...');
      await sleep(800);

      // Stage 3: Downloading (50%)
      setStage('downloading');
      setProgress(50);

      // Parse specific cases input if provided
      const caseNumbers = specificCases
        ? specificCases
            .split(',')
            .map((c) => c.trim())
            .filter((c) => c.length > 0)
        : undefined;

      if (caseNumbers && caseNumbers.length > 0) {
        setCurrentMessage(
          `Downloading ${caseNumbers.length} specific case${caseNumbers.length > 1 ? 's' : ''}...`
        );
        logger.debug('Pulling specific cases (count omitted)');
      } else {
        setCurrentMessage('Downloading cases, emails, and attachments...');
        logger.debug('Pulling cases with default filters');
      }

      const result = await apiService.pullCasesFromDynamics(
        caseNumbers ? { caseNumbers } : undefined
      );

      // Stage 4: Processing (80%)
      setStage('processing');
      setProgress(80);
      setCurrentMessage('Processing downloaded data...');
      await sleep(500);

      // Stage 5: Animate results (80-100%)
      setStage('animating');
      setCaseResults(result.caseResults);
      setSummary({
        casesSuccessful: result.casesSuccessful,
        casesFound: result.casesFound,
        totalEmails: result.totalEmails,
        totalAttachments: result.totalAttachments,
        totalFileSize: result.totalFileSize,
        durationSeconds: result.durationSeconds,
        errors: result.errors,
      });

      // Animate through results
      if (result.caseResults.length > 0) {
        for (let i = 0; i < result.caseResults.length; i++) {
          setAnimatedIndex(i);
          const progressPercent = 80 + ((i + 1) / result.caseResults.length) * 20;
          setProgress(progressPercent);
          setCurrentMessage(
            `Processing case ${i + 1} of ${result.caseResults.length}...`
          );
          await sleep(200);
        }
      } else {
        setProgress(100);
      }

      // Stage 6: Complete
      setStage('complete');
      setProgress(100);
      setCurrentMessage('Download complete!');

      logger.debug('Download complete, notifying parent');
      onSuccess();
    } catch (err: any) {
      logger.error('Download process failed:', err?.message ?? String(err));
      setStage('error');
      setError(err.message || 'Failed to download cases from Dynamics');
      setCurrentMessage('Download failed');
    } finally {
      isDownloadingRef.current = false;
      logger.debug('Download process finished');
    }
  };

  const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const handleClose = () => {
    // Allow closing when:
    // 1. Before download starts (preparing stage and not downloading)
    // 2. After download completes (complete or error)
    if (stage === 'complete' || stage === 'error') {
      onClose(stage === 'complete'); // Pass true if download completed successfully
    } else if (stage === 'preparing' && !isDownloadingRef.current) {
      // User wants to cancel before starting
      onClose(false); // Pass false - no download happened
    }
    // Don't allow closing during active download
  };

  // Check if close button should be enabled
  const canClose =
    stage === 'complete' ||
    stage === 'error' ||
    (stage === 'preparing' && !isDownloadingRef.current);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-neutral-200 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Download className="w-6 h-6 text-primary" />
            <h2 className="text-xl font-semibold text-neutral-900">
              Pull Cases from Dynamics
            </h2>
          </div>
          {/* Always show X button when closing is allowed */}
          {canClose && (
            <button
              onClick={handleClose}
              className="text-neutral-400 hover:text-neutral-600 transition-colors"
              title={stage === 'preparing' ? 'Cancel' : 'Close'}
            >
              <X className="w-6 h-6" />
            </button>
          )}
        </div>

        {/* Content */}
        <div className="px-6 py-6 overflow-y-auto flex-1">
          {/* Specific Cases Input - Always visible but disabled after download starts */}
          {stage !== 'complete' && stage !== 'error' && (
            <div
              className={`mb-6 p-4 rounded-lg border ${
                specificCases
                  ? 'bg-blue-50 border-blue-200'
                  : 'bg-neutral-50 border-neutral-200'
              }`}
            >
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                {specificCases
                  ? `🎯 Pulling Specific Case${specificCases.split(',').filter((c) => c.trim()).length > 1 ? 's' : ''}`
                  : '📦 Pull Mode (Optional)'}
              </label>
              <input
                type="text"
                placeholder="Enter case numbers (e.g., CA-6060133, CA-1234567) or leave blank for bulk pull"
                value={specificCases}
                onChange={(e) => setSpecificCases(e.target.value)}
                className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent disabled:bg-neutral-100 disabled:cursor-not-allowed"
                disabled={isDownloadingRef.current}
              />
              {!isDownloadingRef.current && (
                <>
                  <p className="text-xs text-neutral-600 mt-2 mb-3">
                    💡 <strong>Tip:</strong> Leave blank to pull latest cases, or enter
                    comma-separated case numbers
                  </p>
                  <button
                    onClick={startDownload}
                    disabled={hasDownloadedRef.current}
                    className="w-full bg-primary text-white py-2 px-4 rounded-md hover:bg-primary-600 transition-colors font-medium disabled:bg-neutral-300 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                  >
                    <Download className="w-4 h-4" />
                    <span>Start Download</span>
                  </button>
                </>
              )}
              {isDownloadingRef.current && specificCases && (
                <p className="text-xs text-blue-600 mt-2 font-medium">
                  ✓ Pulling {specificCases.split(',').filter((c) => c.trim()).length}{' '}
                  specific case(s)
                </p>
              )}
            </div>
          )}

          {/* Progress Bar */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-neutral-700">
                {currentMessage}
              </span>
              <span className="text-sm font-medium text-neutral-700">
                {Math.round(progress)}%
              </span>
            </div>
            <div className="w-full bg-neutral-200 rounded-full h-3 overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-300 ${
                  stage === 'error' ? 'bg-red-500' : 'bg-primary'
                }`}
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>

          {/* Error State */}
          {stage === 'error' && error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start space-x-3">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-medium text-red-900 mb-1">Download Failed</h3>
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          )}

          {/* Animated Case Results */}
          {(stage === 'animating' || stage === 'complete') &&
            caseResults.length > 0 && (
              <div className="space-y-3 mb-6">
                <h3 className="text-sm font-semibold text-neutral-700 mb-3">
                  Downloaded Cases:
                </h3>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {caseResults.map((result, index) => (
                    <div
                      key={`${result.caseNumber}-${index}`}
                      className={`flex items-start space-x-3 p-3 rounded-lg border transition-all duration-300 ${
                        index <= animatedIndex
                          ? result.success
                            ? 'bg-green-50 border-green-200 opacity-100'
                            : 'bg-red-50 border-red-200 opacity-100'
                          : 'bg-neutral-50 border-neutral-200 opacity-30'
                      }`}
                    >
                      <CheckCircle
                        className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
                          result.success ? 'text-green-500' : 'text-red-500'
                        }`}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-neutral-900">
                          Case {index + 1}/{caseResults.length}: {result.caseNumber}
                        </p>
                        <div className="flex items-center space-x-4 mt-1 text-xs text-neutral-600">
                          <span className="flex items-center space-x-1">
                            <Mail className="w-3 h-3" />
                            <span>{result.emailsProcessed} emails</span>
                          </span>
                          <span className="flex items-center space-x-1">
                            <Paperclip className="w-3 h-3" />
                            <span>{result.attachmentsDownloaded} attachments</span>
                          </span>
                          {result.totalFileSize > 0 && (
                            <span>({formatFileSize(result.totalFileSize)})</span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

          {/* Summary */}
          {stage === 'complete' && summary && (
            <div className="bg-primary-50 border border-primary-200 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <Database className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h3 className="font-semibold text-primary-900 mb-2">
                    Download Summary
                  </h3>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-neutral-600">Cases downloaded:</span>
                      <span className="ml-2 font-medium text-neutral-900">
                        {summary.casesSuccessful}/{summary.casesFound}
                      </span>
                    </div>
                    <div>
                      <span className="text-neutral-600">Total emails:</span>
                      <span className="ml-2 font-medium text-neutral-900">
                        {summary.totalEmails}
                      </span>
                    </div>
                    <div>
                      <span className="text-neutral-600">Total attachments:</span>
                      <span className="ml-2 font-medium text-neutral-900">
                        {summary.totalAttachments}
                      </span>
                    </div>
                    <div>
                      <span className="text-neutral-600">Total file size:</span>
                      <span className="ml-2 font-medium text-neutral-900">
                        {formatFileSize(summary.totalFileSize)}
                      </span>
                    </div>
                    <div className="col-span-2">
                      <span className="text-neutral-600">Duration:</span>
                      <span className="ml-2 font-medium text-neutral-900">
                        {summary.durationSeconds.toFixed(1)}s
                      </span>
                    </div>
                  </div>
                  {summary.errors && summary.errors.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-primary-200">
                      <p className="text-xs font-medium text-red-700 mb-1">
                        {summary.errors.length} error(s) occurred:
                      </p>
                      <ul className="text-xs text-red-600 space-y-1">
                        {summary.errors.slice(0, 3).map((err: string, idx: number) => (
                          <li key={`error-${idx}-${err.substring(0, 10)}`}>• {err}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        {canClose && (
          <div className="px-6 py-4 border-t border-neutral-200 flex justify-end">
            <button
              onClick={handleClose}
              className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-600 transition-colors font-medium"
            >
              {stage === 'preparing' ? 'Cancel' : 'Close'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PullCasesModal;
